import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            try {
              const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))
              // check for previous reload time not to reload infinitely
              if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
                window.sessionStorage.setItem('nuxt-reload', timeNow)
                window.location.reload(true /* skip cache */)
              }
            } catch {
              // don't throw an error if we have issues reading sessionStorage
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"NUXT_ENV_DEFAULT_LOCALE":"en-US","NUXT_ENV_PARTNER_CLOUDFRONT_URL":"https://d2o5d0ltbm1qhi.cloudfront.net","NUXT_ENV_BOOKING_APP_URL":"https://booking.parking.iflyboise.thecarpark.com","NUXT_ENV_FEATURE_TOGGLE_AP_355_REGIONAL_CONFIG":"false","NUXT_ENV_PAYMENTS_SCRIPT_LINK":"https://uicomponent.live.pci.cavu-tech.com/payments.dev.js","NUXT_ENV_DATADOG_RUM_SESSION_SAMPLE_RATE":"100","NUXT_ENV_LAUNCH_DARKLY_CLIENT_ID":"649ab15d93fe7e13b83aba80","NUXT_ENV_PUSHER_APP_CLUSTER":"eu","NUXT_ENV_REGION_CONFIG_JSON":"{\"code\":\"US\",\"name\":\"United States\",\"domains\":{\"cart\":\"https://service-cart.commerce.cavu-services.com\",\"user\":\"https://service-user.commerce.cavu-services.com\",\"search\":\"https://service-search.product.cavu-services.com\",\"booking\":\"https://service-booking.commerce.cavu-services.com\"},\"pusher\":{\"key\":\"07776ff55e3115bf3169\",\"cluster\":\"mt1\"}}","NUXT_ENV_MA_BASE_URL":"https://account.parking.iflyboise.thecarpark.com/my-profile/","NUXT_ENV_DATADOG_RUM_TRACK_LONG_TASKS":"1","NUXT_ENV_DOMAIN":"parking.iflyboise.thecarpark.com","NUXT_ENV_GTM_SCRIPT_URL":"https://gtm-k6m576t-otk0n.uc.r.appspot.com/gtm.js","NUXT_ENV_DATADOG_RUM_TRACK_USER_INTERACTIONS":"1","NUXT_ENV_AFFILIATE_APPLICATION_TOKEN":"09d0c380-00b2-4475-8187-6b910e93f662","NUXT_ENV_DATADOG_RUM_CLIENT_TOKEN":"pub959120a383438ad4c918930945792714","NUXT_ENV_CHECKOUT_URL":"https://checkout.parking.iflyboise.thecarpark.com","NUXT_ENV_DEFAULT_HERO_PRODUCT":"parking","NUXT_ENV_GTM_ID":"GTM-WCM3ZFH","NUXT_ENV_DATADOG_RUM_TRACK_RESOURCES":"1","NUXT_ENV_DEFAULT_CURRENCY":"USD","NUXT_ENV_PARTNER_CONFIG_JSON":"{\"partner_id\":30,\"channel\":{\"id\":19,\"token\":\"09d0c380-00b2-4475-8187-6b910e93f662\"},\"is_sync\":false,\"poi\":{\"id\":50,\"code\":\"BOI\",\"name\":\"Boise International Airport\",\"type\":\"airport\",\"timezone\":\"America/Boise\",\"terminals\":[],\"country\":{\"code\":\"US\",\"name\":\"United States\"}},\"terminals\":[],\"pois\":[{\"id\":50,\"code\":\"BOI\",\"name\":\"Boise International Airport\",\"type\":\"airport\",\"timezone\":\"America/Boise\",\"terminals\":[],\"country\":{\"code\":\"US\",\"name\":\"United States\"}}],\"partner_name\":\"Boise\",\"company\":{\"name\":\"The Car Park Company\",\"address\":\"3201 Airport Way, Suite 1000, Boise, Idaho, 83705\",\"registered_number\":null},\"brand_color\":null,\"colours\":{\"cta\":{\"action_button_font\":\"light\",\"action_button_background\":\"0,181,226,1\",\"action_button_font_colour\":null},\"brand\":\"0,181,226,1\",\"footer\":{\"font\":\"dark\",\"background\":\"0,181,226,0.08\"},\"header\":{\"font\":\"dark\",\"background\":\"255,255,255,1\",\"progress_circles\":\"0,181,226,1\"}},\"logo\":\"https://cdn.partners.product.cavu-services.com/images/the-car-park-company_logo_64d8d186-e4d3-44fa-8e90-236111e54ad8.svg\",\"logo_redirect_url\":\"iflyboise.com\",\"favicon\":\"https://cdn.partners.product.cavu-services.com/images/the-car-park-company_favicon_32e88613-aacc-405a-8266-cbd0e7aca477.ico\",\"gtm_id\":\"GTM-WCM3ZFH\",\"default_currency\":\"USD\",\"partner_type_code\":\"provider_and_white_label\",\"currencies\":[{\"code\":\"USD\",\"name\":\"United States Dollar\",\"sign\":\"$\"}],\"default_language\":\"en-US\",\"languages\":[{\"code\":\"en-US\",\"iso\":\"en-US\",\"file\":\"en-US.js\",\"name\":\"English\",\"name_native\":\"English\",\"region\":\"United States\",\"region_code\":\"US\"}],\"page_links\":{\"domain\":\"iflyboise.thecarpark.com\",\"whitelabel_host_domains\":{\"account\":\"account.parking.iflyboise.thecarpark.com\",\"checkout\":\"checkout.parking.iflyboise.thecarpark.com\",\"ecommerce\":\"booking.parking.iflyboise.thecarpark.com\"}},\"page_texts\":{\"terms_and_conditions\":{\"type\":\"link\",\"value\":{\"en-US\":\"https://thecarpark.com/Portals/0/AGREEMENT%20BETWEEN%20USER%20AND%20The%20Car%20Park_Cavu_10_6_2022.pdf\"}},\"privacy_policy\":{\"type\":\"link\",\"value\":{\"en-US\":\"https://thecarpark.com/Privacy/\"}},\"cookie_policy\":{\"type\":\"link\",\"value\":{\"en-US\":\"https://thecarpark.com/Privacy/\"}}},\"payments\":[{\"id\":17,\"is_active\":true,\"payment_provider_id\":1,\"merchant_id\":\"stripe-906289d2377e48919c1f8fdbd332f913\",\"config\":{\"statement_descriptor\":\"TheCarPark BOI\"}}],\"auth0_client_id\":\"GRxL12H1Y01FeVmg2bm3Ckqac4mdgAYA\",\"primary_product_type\":\"parking\",\"product_types_available\":[\"parking\"],\"products\":{\"parking\":{\"product_code\":\"parking\",\"customer_groups\":null,\"airlines_list\":[]}},\"customer_support_contact_details\":{\"email\":null,\"phone_number\":null,\"allowed_pages\":{\"modify\":false,\"account\":false,\"receipt\":false}},\"migration_support\":{\"is_in_progress\":false,\"manage_booking_url\":null},\"marketing_preferences\":null,\"is_results_filtering_enabled\":false,\"is_results_sorting_enabled\":false,\"is_product_logo_visible\":false,\"is_tax_inclusive\":true,\"is_fee_inclusive\":true,\"discount_visibility_rules\":{\"is_enabled_in_landing_page\":false,\"is_enabled_in_results_page\":false,\"is_enabled_in_payment_page\":true},\"review_providers\":[{\"name\":\"Feefo\",\"is_enabled\":false,\"merchant_id\":null}],\"page_title\":{\"en-US\":\"Book your parking at the Boise Airport now!\"},\"page_meta_title\":\"Boise Airport Parking\",\"page_meta_description\":\"Boise Airport Parking\",\"is_map_view_enabled\":false,\"is_show_price_per_day_enabled\":null,\"is_sell_bundles_enabled\":false,\"choose_your_product_text\":{\"en-US\":\"Boise Airport Parking\"},\"is_multi_basket\":false,\"search_criteria\":{\"picker_type\":\"one\",\"picker1_label\":{\"en-US\":\"Date/Time\"},\"is_picker_descriptions_enabled\":false},\"airlines_visibility_rules\":{\"is_enabled_in_landing_page\":false},\"baggage_visibility_rules\":{\"is_enabled_in_landing_page\":false},\"infrastructure_region\":\"US\",\"cashback_provider\":{\"tag_hash\":null},\"partnerize\":{\"is_active\":false,\"partner_id\":null},\"is_vehicle_lookup_enabled\":false,\"is_flight_lookup_enabled\":false,\"auth0\":{\"is_enabled\":false,\"domain\":\"cavu-eu-web-dev.eu.auth0.com\",\"cookie_domain\":null,\"audience\":null,\"logout_redirect\":null},\"annual_platform_fee\":0,\"psp_fee\":2.99}","NUXT_ENV_ACCOUNT_URL":"https://account.parking.iflyboise.thecarpark.com/","NUXT_ENV_DATADOG_RUM_SERVICE":"white-label-ecommerce","NUXT_ENV_DATADOG_RUM_SITE":"datadoghq.eu","NUXT_ENV_DATADOG_RUM_ENVIRONMENT":"live","NUXT_ENV_ENVIRONMENT":"live","NUXT_ENV_DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE":"20","NUXT_ENV_DATADOG_RUM_APPLICATION_ID":"dc6daa61-a46e-4ae0-898c-c40863080e01","NUXT_ENV_PUSHER_APP_KEY":"fba31391b89e1043cd86","NUXT_ENV_FEATURE_TOGGLE_ACCOUNT_LOGIN_ENABLE":"0","NUXT_ENV_DATADOG_RUM_DEFAULT_PRIVACY_LEVEL":"mask-user-input","NUXT_ENV_CHECKOUT_BASE_URL":"https://checkout.parking.iflyboise.thecarpark.com/","NUXT_ENV_ECOMMERCE_URL":"https://booking.parking.iflyboise.thecarpark.com/"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
